/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : April 30, 2025
Folder          : src/components/homepage/index
File            : Homepage.tsx
Description     : 
    This component allows the user to access the homepage of the web app.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/



// Import React Systems
import { useState } from 'react'
import { collection, setDoc, doc, serverTimestamp, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../../../backend/firebaseConfig";

// Imports MUI Components
import {
  Box,
  Grid,
  Link,
  Stack,
  Card,
  CardMedia,
  CardContent,
  Button,
  Snackbar,
  Alert
} from "@mui/material"

import Text from "../../../atoms/text/Text"
import Icon from "../../../atoms/icon/Icon"
import ButtonComp from "../../../atoms/button/Button"
import TextFieldComp from "../../../atoms/text-fields/TextFieldComp"


import call from "../../../../../assets/call.png"
import email from "../../../../../assets/email.png"
import facebook from "../../../../../assets/facebook_dark.png"
import instagram from "../../../../../assets/instagram_dark.png"
import tiktok from "../../../../../assets/tiktok_dark.png"
import location from "../../../../../assets/location.png"
import important from "../../../../../assets/important.png"
import hours from "../../../../../assets/time.png"

import "./styles.css"

const Section = ({ onSent, userUid
}) => {


  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailField, setEmailField] = useState("");
  const [messageField, setMessageField] = useState("");

  const [sending, setSending] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const closeSnack = () => setSnackOpen(false);

  const gridStylesContainer = {
    backgroundColor: "transparent",
    justifyItems: "center",
    alignItems: "center",
    alignContent: "center",
    padding: {
      xs: "80px 20px 80px 20px", 
      sm: "80px 30px 80px 30px", 
      md: "80px 110px 80px 110px", 
      lg: "80px 110px 80px 110px", 
      xl: "80px 110px 80px 110px" 
    },
    width: "100%",
    "& .MuiGrid-item": {
      display: "flex",
      justifyContent: "center",
    },
  }

  

  const makeOnChange = (setter) => (e) => {
     const v = e && (typeof e === "object" && "target" in e) ? e.target?.value : e;
     setter(typeof v === "undefined" ? "" : v);
   };

  const handleSendMessage = async (formValues) => {
    try {
      // basic client-side validation
      if (!formValues.message || !formValues.email) {
        setSnackMsg("Please provide at least an email and a message.");
        setSnackSeverity("warning");
        setSnackOpen(true);
        return;
      }

      setSending(true);

      // Build canonical message: "{name} ({email}) sent a message: {message}"
      const senderName = (formValues.name || "").trim();
      const senderEmail = (formValues.email || "").trim();
      const composedMessage = `${senderName ? senderName + " " : ""}(${senderEmail}) sent a message: ${formValues.message}`;

      // 1) Create root notification (central record)
      const rootPayload = {
        message: composedMessage,
        name: senderName,
        email: senderEmail,
        type: "contact",
        recipientRole: ["DentistOwner", "ClinicStaff"],
        read: false,
        createdAt: serverTimestamp(),
      };

  
     // Find DentistOwner and ClinicStaff users and send notifications
     try {
       // Send to both DentistOwner and ClinicStaff users
       const staffQ = query(collection(db, "users"), where("role", "in", ["DentistOwner", "ClinicStaff"]));
       const staffSnap = await getDocs(staffQ);
       
       if (staffSnap.empty) {
         console.warn("No DentistOwner or ClinicStaff users found — per-recipient write skipped.");
       } else {
         const writes = staffSnap.docs.map((d) => {
           const uid = d.id;
           const userData = d.data();
           const role = userData.role || "ClinicStaff";
           
           if (!uid) return Promise.resolve();
           const perRecipientPayload = {
             message: composedMessage,
             name: senderName || null,
             email: senderEmail || null,
             type: "contact",
             read: false,
             recipientRole: role,
             createdAt: serverTimestamp(),
           };
           // generate an auto-id child doc under notifications/{uid}/userNotifications
           const childColl = collection(db, "notifications", uid, "userNotifications");
           const childDocRef = doc(childColl); // only generates id
           return setDoc(childDocRef, perRecipientPayload)
             .then(() => {
               // optimistic UI update: notify parent Dashboard to prepend this notification
               try {
                 window.dispatchEvent(new CustomEvent("notification-sent", {
                   detail: {
                     id: childDocRef.id,
                     uid: uid,
                     message: perRecipientPayload.message,
                     name: perRecipientPayload.name || null,
                     email: perRecipientPayload.email || null,
                     type: perRecipientPayload.type || "contact",
                     recipientRole: perRecipientPayload.recipientRole || role,
                     read: perRecipientPayload.read || false,
                     createdAt: new Date(), // UI-only until server timestamp arrives
                     __local: true
                   }
                 }));
               } catch (e) {
                 console.warn("Section.onSent callback failed", e);
               }
             })
             .catch((err) => {
               console.error(`Failed to send notification to ${role}:`, err);
             });
         });

         await Promise.allSettled(writes);
       }
     } catch (err) {
       console.error("Per-recipient write failed:", err);
     }
      

      // show success to user and clear form
      setSnackMsg("Message sent. Thank you!");
      setSnackSeverity("success");
      setSnackOpen(true);
      setFirstName("");
      setMiddleName("");
      setLastName("");
      setEmailField("");
      setMessageField("");
    } catch (err) {
      console.error("Send message error:", err);
      setSnackMsg("Failed to send message. Try again.");
      setSnackSeverity("error");
      setSnackOpen(true);
    } finally {
      setSending(false);
    }
  };

  

  const boxStylesContainer = {
    height: "100%",
    justifyContent: "center",
    borderRadius: "20px",
    backgroundColor: "transparent",
  }

  const cardStyleContainer = {
    textAlign: "center",
    padding: "10px",
    borderRadius: "20px",
    backgroundColor: "#FEFEFE",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
  }

  const cardStyleLeft = {
    padding: "10px",
    borderRadius: "20px",
    backgroundColor: "#FEFEFE",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
  }


  
  

  return (
    <>
      {/*Grids */}
      <Grid
        container
        rowSpacing={4}
        columnSpacing={6}
        sx={gridStylesContainer}
      >

        <Grid 
          size={{
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12,
            xl: 8
          }} item
        >

          {/*Grids */}
          <Grid
            container
            rowSpacing={4}
            columnSpacing={6}
          >

            {/* Content */}
            {[
              { 
                img: call,
                title: "Call Us",
                caption: "(032) 326 1584" 
              },
              { 
                img: email,
                title: "Email Us",
                caption: "vsdiamonddentalclinic@gmail.com"
              },
              { 
                img: {facebook, instagram, tiktok},
                title: "Follow Us",
                caption: "socials"
              },
            ].map((content) => (
              <>

                <Grid 
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 12,
                    lg: 4,
                    xl: 4 
                  }} item
                >

                  <Box
                    sx={boxStylesContainer}
                  >

                    <Card
                      elevation={0}
                      sx={cardStyleContainer}
                    >

                      {content.caption !== "socials" ? (
                        <Icon
                          src={content.img}
                          size="large"/>
                      ) : (null)}
                        
                      <CardContent>

                        <Stack
                          direction="column"
                          spacing={2}
                        >

                          <Text
                            text={content.title}
                            variant="h3"/>

                          {content.caption !== "socials" ? (
                            <Text
                              text={content.caption}
                              variant="body1"/>
                            ) : (
                              <Text
                                text={content.caption}
                                variant="spacer"/>
                            )}

                        </Stack>

                        {content.caption === "socials" ? (
                          <Stack
                            direction="row"
                            spacing={2}
                            padding="10px"
                            justifyContent="space-around"
                          >
                            <Icon
                              src={content.img.facebook}
                              size="large"/>
                            <Icon
                              src={content.img.instagram}
                              size="large"/>
                            <Icon
                              src={content.img.tiktok}
                              size="large"/>
                          </Stack>
                        ) : (null)}

                      </CardContent>
                                    
                    </Card>
                
                  </Box>

                </Grid>

              </>
            ))}

            <Grid 
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
                xl: 12 
              }} item
            >

              <Box
                sx={boxStylesContainer}
              >

                <Card
                  elevation={0}
                  sx={cardStyleLeft}
                >
                  <CardContent>

                    <Stack
                      direction="column"
                      spacing={2}
                    >

                      <Text
                        text="Send Us a Message"
                        variant="h3"/>

                      {/*Grids */}
                      <Grid
                        container
                        rowSpacing={4}
                        columnSpacing={3}
                      >
                        <Grid 
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 4,
                            lg: 4,
                            xl: 4 
                          }} item
                        >

                          <Box
                            sx={boxStylesContainer}
                          >
                            
                            {/* TextField Email/Mobile Number Container*/}
                            <TextFieldComp
                              placeholder="Your first name"
                              id="firstName"
                              label="First Name"
                              type="text"
                              value={firstName}
                              color="primary"
                              onChange={makeOnChange(setFirstName)}
                              state={false}
                              requires={true}/>

                          </Box>
                            
                        </Grid>

                        <Grid 
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 4,
                            lg: 4,
                            xl: 4 
                          }} item
                        >

                          <Box
                            sx={boxStylesContainer}
                          >
                            
                            {/* TextField Email/Mobile Number Container*/}
                            <TextFieldComp
                              placeholder="Your middle name"
                              id="middleName"
                              label="Middle Name"
                              type="text"
                              value={middleName}
                              color="primary"
                              onChange={makeOnChange(setMiddleName)}
                              state={false}
                              requires={false}/>

                          </Box>
                            
                        </Grid>

                        <Grid 
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 4,
                            lg: 4,
                            xl: 4 
                          }} item
                        >

                          <Box
                            sx={boxStylesContainer}
                          >
                            
                            {/* TextField Email/Mobile Number Container*/}
                            <TextFieldComp
                              placeholder="Your last name"
                              id="lastName"
                              label="Last Name"
                              type="text"
                              value={lastName}
                              color="primary"
                              onChange={makeOnChange(setLastName)}
                              state={false}
                              requires={true}/>

                          </Box>
                            
                        </Grid>

                        <Grid 
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12
                          }} item
                        >

                          <Box
                            sx={boxStylesContainer}
                          >
                            
                            {/* TextField Email/Mobile Number Container*/}
                            <TextFieldComp
                              placeholder="Your email"
                              id="email"
                              label="Email"
                              type="text"
                              value={emailField}
                              color="primary"
                              onChange={makeOnChange(setEmailField)}
                              state={false}
                              requires={true}/>

                          </Box>
                            
                        </Grid>

                        <Grid 
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12
                          }} item
                        >

                          <Box
                            sx={boxStylesContainer}
                          >
                            
                            {/* TextField Email/Mobile Number Container*/}
                            <TextFieldComp
                              placeholder="Your message"
                              id="message"
                              label="Message"
                              type="text"
                              value={messageField}
                              color="primary"
                              onChange={makeOnChange(setMessageField)}
                              state={false}
                              requires={true}/>

                          </Box>
                            
                        </Grid>

                        <Grid 
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12
                          }} item
                        >

                          <Box
                            sx={boxStylesContainer}
                          >
                            
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                              <Button
                                variant="contained"
                                onClick={() =>
                                  handleSendMessage({
                                    name: `${firstName} ${middleName} ${lastName}`.trim(),
                                    email: emailField,
                                    message: messageField,
                                  })
                                }
                                disabled={sending}
                                sx={{
                                  textTransform: "none",
                                  px: 3,
                                  py: 1,
                                  backgroundColor: sending ? "rgba(0,0,0,0.12)" : "#2C6785",
                                  color: "#fff",
                                  "&:hover": { backgroundColor: sending ? "rgba(0,0,0,0.12)" : "#244f63" },
                                }}
                              >
                                {sending ? "Sending..." : "Send Message"}
                              </Button>
                            </Box>

          <Snackbar open={snackOpen} autoHideDuration={4000} onClose={closeSnack} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
       <Alert onClose={closeSnack} severity={snackSeverity} sx={{ width: "100%" }}>
          {snackMsg}
        </Alert>
      </Snackbar>

   

                          </Box>
                            
                        </Grid>
                        
                      </Grid>

                    </Stack>

                  </CardContent>
                                  
                </Card>
              
              </Box>

            </Grid>

          </Grid>
        </Grid>

        <Grid 
          size={{
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12,
            xl: 4
          }} item
        >

          {/*Grids */}
          <Grid
            container
            rowSpacing={4}
            columnSpacing={6}
          >

            {/* Content */}
            {[
              { 
                img: location,
                title: "Location",
                caption: "2/F, 8 Banawa Centrale, R. Duterte St. Banawa, Cebu City (Above Marketplace) Cebu, Philippines" 
              },
              { 
                img: important,
                title: "Emergency Care",
                caption: "For dental emergencies outside of regular hours, please call our hotline."
              },
              { 
                img: hours,
                title: "Office Hours",
                row1col1: "Monday - Friday",
                row1col2: "9AM-6PM",
                row2col1: "Saturday",
                row2col2: "By Appointment",
                row3col1: "Sunday",
                row3col2: "Closed",
              },
            ].map((content) => (
              <>

                <Grid 
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 12,
                    lg: 12,
                    xl: 12 
                  }} item
                >

                  <Box
                    sx={boxStylesContainer}
                  >

                    <Card
                      elevation={0}
                      sx={cardStyleLeft}
                    >
                      
                      <CardContent>

                        <Stack
                          direction="column"
                          spacing={2}
                        >

                          <Stack
                            direction="row"
                            alignItems="center"
                          >
                            <Icon
                              src={content.img}
                              size="large"/>

                            <Text
                              text={content.title}
                              variant="h3"/>

                          </Stack>

                          {content.title === "Office Hours" ? (
                            <>
                              <Stack
                                direction="row"
                                justifyContent="space-between">
                                <Text
                                  text={content.row1col1}
                                  variant="body1"/>
                                <Text
                                  text={content.row1col2}
                                  variant="body1"/>
                              </Stack>
                              <Stack
                                direction="row"
                                justifyContent="space-between">
                                <Text
                                  text={content.row2col1}
                                  variant="body1"/>
                                <Text
                                  text={content.row2col2}
                                  variant="contactHours"/>
                              </Stack>
                              <Stack
                                direction="row"
                                justifyContent="space-between">
                                <Text
                                  text={content.row3col1}
                                  variant="body1"/>
                                <Text
                                  text={content.row3col2}
                                  variant="contactHours"/>
                              </Stack>
                            </>
                          ) : (
                            <Text
                              text={content.caption}
                              variant="body1"/>
                          )}

                        </Stack>

                      </CardContent>
                                  
                    </Card>
              
                  </Box>

                </Grid>

              </>
            ))}

          </Grid>

        </Grid>
      </Grid>
    </>
  )

}

export default Section;